import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "skarbnik:selectedSchoolYearId";

const SchoolYearContext = createContext(null);

function sortSchoolYears(years = []) {
  return [...years].sort((a, b) => {
    const aLabel = getYearLabel(a);
    const bLabel = getYearLabel(b);
    return bLabel.localeCompare(aLabel, "pl");
  });
}

function getYearLabel(year) {
  return year?.label ?? year?.year_label ?? year?.name ?? year?.school_year ?? "";
}

export function SchoolYearProvider({ children }) {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [schoolYears, setSchoolYears] = useState([]);
  const [selectedSchoolYearId, setSelectedSchoolYearIdState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshSchoolYears = useCallback(async () => {
    if (!user) {
      setSchoolYears([]);
      setSelectedSchoolYearIdState(null);
      return;
    }

    setLoading(true);
    setError(null);

    let query = supabase
      .from("school_years")
      .select("*");

    if (!isAdmin) {
      query = query.neq("status", "draft");
    }

    const { data, error } = await query;

    if (error) {
      setSchoolYears([]);
      setSelectedSchoolYearIdState(null);
      setError(error);
      setLoading(false);
      return;
    }

    const visibleYears = sortSchoolYears(data ?? []).filter(
      (year) => isAdmin || year.status !== "draft"
    );

    setSchoolYears(visibleYears);

    setSelectedSchoolYearIdState((currentId) => {
      const storedId = localStorage.getItem(STORAGE_KEY);
      const preferredId = currentId ?? storedId;
      const preferred = visibleYears.find(
        (year) => String(year.id) === String(preferredId)
      );
      const active = visibleYears.find((year) => year.status === "active");
      const next = preferred ?? active ?? visibleYears[0] ?? null;

      if (next?.id) {
        localStorage.setItem(STORAGE_KEY, String(next.id));
        return next.id;
      }

      localStorage.removeItem(STORAGE_KEY);
      return null;
    });

    setLoading(false);
  }, [isAdmin, user]);

  useEffect(() => {
    if (authLoading) return;
    refreshSchoolYears();
  }, [authLoading, refreshSchoolYears]);

  const setSelectedSchoolYearId = useCallback(
    (id) => {
      const next = schoolYears.find((year) => String(year.id) === String(id));
      if (!next) return;
      localStorage.setItem(STORAGE_KEY, String(next.id));
      setSelectedSchoolYearIdState(next.id);
    },
    [schoolYears]
  );

  const activeSchoolYear = useMemo(
    () => schoolYears.find((year) => year.status === "active") ?? null,
    [schoolYears]
  );

  const selectedSchoolYear = useMemo(
    () =>
      schoolYears.find((year) => String(year.id) === String(selectedSchoolYearId)) ??
      null,
    [schoolYears, selectedSchoolYearId]
  );

  const value = {
    schoolYears,
    activeSchoolYear,
    selectedSchoolYear,
    selectedSchoolYearId,
    setSelectedSchoolYearId,
    isSelectedYearActive: selectedSchoolYear?.status === "active",
    isSelectedYearArchived: selectedSchoolYear?.status === "archived",
    isSelectedYearDraft: selectedSchoolYear?.status === "draft",
    loading,
    error,
    refreshSchoolYears,
    getYearLabel,
  };

  return (
    <SchoolYearContext.Provider value={value}>
      {children}
    </SchoolYearContext.Provider>
  );
}

export function useSchoolYear() {
  const context = useContext(SchoolYearContext);
  if (!context) {
    throw new Error("useSchoolYear must be used within SchoolYearProvider");
  }
  return context;
}

export function buildYearSettings(settings, schoolYear) {
  const openingBalance = Number(schoolYear?.opening_balance ?? 0);
  return {
    ...(settings ?? {}),
    opening_balance: openingBalance,
    start_balance: openingBalance,
  };
}

export function getSchoolYearLabel(year) {
  return getYearLabel(year);
}
